import { useQueryClient } from '@tanstack/react-query';
import { useAppForeground } from '@/hooks/useAppForeground';

/**
 * SAB-996: the draft room opts OUT of the app-wide focus invalidation.
 *
 * The room holds its own realtime channel and is already current; a blanket invalidation
 * during a live draft is work nobody asked for at the moment latency matters most.
 *
 * To be exact about what this does and does not fix: it is NOT the cause of the alt-tab
 * blank. The pool is plain useState (setAvailablePlayers), not a query, so
 * invalidateQueries() never reached it -- measured at 0 extra pool reads across a focus
 * event in draftFocusRefetch.test.tsx. The blank came from the auth-refresh identity
 * cascade fixed in NFLLeagueContext. This is a separate, smaller saving.
 *
 * The NFL room is a TAB, not a path (`/nfl?tab=draft`), so the search param has to be read
 * as well as the pathname -- matching on the path alone would silently miss the room this
 * is meant to exclude. `/draft-room/` is the MLB room.
 *
 * ── WHY window.location AND NOT useLocation ─────────────────────────────────
 *
 * This component is mounted at App.tsx:120, OUTSIDE the <Router>. The first version used
 * useLocation() and took the whole app down with "useLocation() may be used only in the
 * context of a <Router> component" -- every route, not just the draft room. typecheck and
 * 1,137 unit tests all passed; only opening a browser found it.
 *
 * Reading window.location inside the callback is also the more correct reading: it resolves
 * at the instant focus fires rather than at the render that installed the handler.
 */
function isDraftRoom(pathname: string, search: string): boolean {
  if (pathname.startsWith('/draft-room')) return true;
  if (pathname === '/nfl' || pathname === '/nfl/staging') {
    return new URLSearchParams(search).get('tab') === 'draft';
  }
  return false;
}

/**
 * SAB-979: app-wide react-query refresh when the app returns to the foreground.
 *
 * The QueryClient sets `refetchOnWindowFocus: false` globally, which is the right default
 * for a fantasy app full of polling screens but wrong for the one case that matters here:
 * a user who left to pay or verify and came back to a balance the server already changed.
 *
 * This is the react-query HALF. The wallet balance and KYC status are not react-query --
 * they re-read through useAppForeground directly in their own components. Mounted once,
 * app-wide, so a surface added later inherits the behaviour without remembering to.
 *
 * Invalidate rather than refetch: react-query refetches only what is currently mounted and
 * observed, so an unmounted screen is marked stale and re-reads when it next renders,
 * instead of firing a request for a screen nobody is looking at.
 */
export default function ForegroundRefetch() {
  const queryClient = useQueryClient();
  useAppForeground(() => {
    if (isDraftRoom(window.location.pathname, window.location.search)) return;
    queryClient.invalidateQueries();
  });
  return null;
}

export { isDraftRoom };
